import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react'
import { api, apiUrl } from '../lib/api'
import type { Features, MeResponse, User } from '../lib/types'

// Server feature flags default to all-off until /me says otherwise, so dark
// or older payloads render the same UI as a flagged-off server (§3.7).
const NO_FEATURES: Features = { group_wallet: false }

interface AuthContextValue {
  isAuthenticated: boolean
  isLoading: boolean
  user: User | null
  /** Server feature flags from the /me envelope (§3.7); all-off when absent. */
  features: Features
  /** Kicks off the Steam OpenID server redirect flow (NOT a client-held token). */
  signInWithSteam: () => void
  signOut: () => Promise<void>
  refreshSession: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [features, setFeatures] = useState<Features>(NO_FEATURES)
  const [isLoading, setIsLoading] = useState(true)

  const refreshSession = useCallback(
    () =>
      api
        .get<MeResponse>('/me')
        .then((data) => {
          setUser(data.user ?? null)
          setFeatures(data.features ?? NO_FEATURES)
        })
        .catch(() => {
          setUser(null)
          setFeatures(NO_FEATURES)
        })
        .finally(() => {
          setIsLoading(false)
        }),
    [],
  )

  useEffect(() => {
    refreshSession()
  }, [refreshSession])

  // Sign-in is a full-page navigation to the backend OpenID start endpoint;
  // the client never performs OpenID in JS or holds a token.
  const signInWithSteam = useCallback(() => {
    window.location.href = apiUrl('/auth/steam')
  }, [])

  const signOut = useCallback(async () => {
    try {
      await api.delete('/session')
    } catch {
      // Clear local state even if the server call fails.
    }
    setUser(null)
  }, [])

  // Memoized because this value is read by route LAYOUTS, and a layout that
  // re-renders re-fires any <Navigate> it holds (see RedirectOnce). Built
  // inline, this object was new on every root render, so every useAuth()
  // consumer invalidated together — harmless on a leaf, the difference between
  // safe and looping in a gate. ClubMembershipContext already does this.
  const value = useMemo(
    () => ({
      isAuthenticated: user !== null,
      isLoading,
      user,
      features,
      signInWithSteam,
      signOut,
      refreshSession,
    }),
    [user, isLoading, features, signInWithSteam, signOut, refreshSession],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
